import React, { useRef } from 'react';
import './Home.css';
import { Link } from 'react-router-dom';
import { trackCTAClick } from '../utils/analytics';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import {
  FaFileInvoiceDollar,
  FaChartLine,
  FaRobot,
  FaCogs,
  FaCloud,
  FaArrowRight,
  FaCheckCircle,
  FaLightbulb,
  FaLaptopCode,
  FaShieldAlt,
  FaSearchDollar,
  FaHandshake,
  FaClipboardCheck,
  FaRocket,
  FaBuilding,
  FaShoppingCart,
  FaIndustry,
  FaUniversity,
  FaUserTie,
  FaUsers,
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: reduceMotion ? 0 : 0.45, delay: reduceMotion ? 0 : delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

function Home() {
  return (
    <div className="home-redesign">
      <Helmet>
        <title>Media Mars - IT Strategy, Automation, SpocoDoc & Digital Transformation | UK</title>
        <meta name="description" content="Media Mars helps UK organisations fix inefficient processes, gain control of spend, and turn disconnected systems into competitive advantage - with IT strategy, automation, data analytics, and SpocoDoc." />
        <meta name="keywords" content="SpocoDoc, budget management software, invoice approval software UK, IT strategy review UK, IT consulting for SMEs, Power BI consulting UK, business process automation, ERP CRM integration, reduce IT costs SME, automate invoice approvals, Power Platform development, Azure Logic Apps automation, custom app development UK" />
        <meta property="og:title" content="Media Mars - IT Strategy, Automation & SpocoDoc Platform" />
        <meta property="og:description" content="We help UK organisations fix processes, control spend, and connect systems - with IT strategy, automation, data analytics and our SpocoDoc platform." />
        <meta property="og:url" content="https://www.media-mars.com/" />
        <meta property="og:image" content="https://www.media-mars.com/MediaMarsLogo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Media Mars - IT Strategy, Automation & SpocoDoc Platform" />
        <meta name="twitter:description" content="We help UK organisations fix processes, control spend, and connect systems - with IT strategy, automation, data analytics and our SpocoDoc platform." />
        <meta name="twitter:image" content="https://www.media-mars.com/MediaMarsLogo.png" />
        <link rel="canonical" href="https://www.media-mars.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Media Mars Limited",
            "description": "UK IT consulting firm providing strategy, automation, data analytics, custom solutions, and the SpocoDoc SaaS platform for budget and invoice management.",
            "url": "https://www.media-mars.com/",
            "logo": "https://www.media-mars.com/MediaMarsLogo.png",
            "image": "https://www.media-mars.com/MediaMarsLogo.png",
            "foundingDate": "2014",
            "address": { "@type": "PostalAddress", "addressCountry": "GB" },
            "areaServed": ["GB", "EU", "IN"],
            "serviceType": ["IT Strategy Consulting", "Business Process Automation", "Data Analytics", "System Integration", "Custom Software Development", "SpocoDoc SaaS Platform", "IT Coaching", "Hardware Supply"],
            "contactPoint": { "@type": "ContactPoint", "contactType": "sales", "email": "info@media-mars.com", "areaServed": "GB" }
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="hero-wide">
        <img className="hero-wide__background" src="/Images/Home/Banner/earth-network.png" alt="" aria-hidden="true" decoding="async" fetchPriority="high" />
        <div className="hero-wide__slogan" aria-hidden="true">
          <span></span>
          <strong>Solving<br />today.</strong>
          <strong>Building<br /><em>tomorrow.</em></strong>
        </div>
        <div className="hero-wide__inner">
          <motion.div
            className="hero-wide__text"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <span className="hero-wide__label">People. Technology. Progress.</span>
            <h1>
              <span>Practical technology</span>
              <span>for real <span className="hero-wide__highlight">business impact</span></span>
            </h1>
            <p className="hero-wide__sub">
              We help organisations solve today's operational challenges while building the connected,
              automated foundations they need for tomorrow.
            </p>
            <div className="hero-service-strip" aria-label="Media Mars capabilities">
              <span><i><FaCloud /></i>Cloud &amp; Infrastructure</span>
              <span><i><FaCogs /></i>Business Applications</span>
              <span><i><FaRobot /></i>Automation &amp; AI</span>
              <span><i><FaShieldAlt /></i>Security &amp; Compliance</span>
              <span><i><FaHandshake /></i>Advisory &amp; Delivery</span>
            </div>
            <div className="hero-wide__ctas">
              <Link to="/contact" className="btn-primary" onClick={() => trackCTAClick('Book a Free Consultation', 'hero')}>Book a Free Consultation <FaArrowRight /></Link>
              <Link to="/services" className="btn-outline" onClick={() => trackCTAClick('See How We Help', 'hero')}>See How We Help</Link>
            </div>
          </motion.div>
          <motion.div
            className="hero-wide__visual"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="orbital-graphic" aria-hidden="true"></div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES OVERVIEW — compact icon grid */}
      <section className="services-overview">
        <div className="section-inner">
          <FadeIn>
            <span className="section-tag">What We Do</span>
            <h2 className="section-title">Technology Solutions That Drive Results</h2>
            <p className="section-subtitle">From strategy to implementation, we cover the full spectrum of IT challenges facing UK businesses.</p>
          </FadeIn>
          <div className="services-grid">
            <FadeIn className="service-tile" delay={0.04}>
              <div className="service-tile__icon"><FaLightbulb /></div>
              <h3>IT Strategy & Advisory</h3>
              <p>Independent reviews for CEOs and CFOs to map every IT pound to business outcomes.</p>
              <Link to="/services#it-strategy" className="link-arrow">Learn More <FaArrowRight /></Link>
            </FadeIn>
            <FadeIn className="service-tile" delay={0.08}>
              <div className="service-tile__icon"><FaRobot /></div>
              <h3>Automation</h3>
              <p>Eliminate manual tasks with Power Automate, Azure Functions, and intelligent workflows that save hours weekly.</p>
              <Link to="/services#automation" className="link-arrow">Learn More <FaArrowRight /></Link>
            </FadeIn>
            <FadeIn className="service-tile" delay={0.12}>
              <div className="service-tile__icon"><FaChartLine /></div>
              <h3>Data & Analytics</h3>
              <p>Power BI dashboards, automated pipelines, and a single source of truth for confident decision-making.</p>
              <Link to="/services#data-analytics" className="link-arrow">Learn More <FaArrowRight /></Link>
            </FadeIn>
            <FadeIn className="service-tile" delay={0.16}>
              <div className="service-tile__icon"><FaCogs /></div>
              <h3>System Integration</h3>
              <p>Connect ERP, CRM, eCommerce, and finance systems so every department sees the same data.</p>
              <Link to="/services#integration" className="link-arrow">Learn More <FaArrowRight /></Link>
            </FadeIn>
            <FadeIn className="service-tile" delay={0.2}>
              <div className="service-tile__icon"><FaLaptopCode /></div>
              <h3>Custom Development</h3>
              <p>Bespoke applications on Power Platform, Azure, React, and .NET - built for your actual workflows.</p>
              <Link to="/services#apps" className="link-arrow">Learn More <FaArrowRight /></Link>
            </FadeIn>
            <FadeIn className="service-tile" delay={0.24}>
              <div className="service-tile__icon"><FaFileInvoiceDollar /></div>
              <h3>SpocoDoc Platform</h3>
              <p>Budget management, invoice processing, expense tracking, and multi-level approvals in one digital workspace.</p>
              <Link to="/services#spocodoc" className="link-arrow">Learn More <FaArrowRight /></Link>
            </FadeIn>
          </div>
          <FadeIn>
            <div className="services-overview__cta">
              <Link to="/services" className="btn-primary" onClick={() => trackCTAClick('Explore All Solutions', 'services-overview')}>Explore All Solutions <FaArrowRight /></Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SPOCODOC SPOTLIGHT — condensed product highlight */}
      <section className="spocodoc-spotlight" id="spocodoc-spotlight">
        <div className="spocodoc-spotlight__inner">
          <FadeIn className="spocodoc-spotlight__text">
            <div className="spocodoc-logo" aria-label="SpocoDoc">
              <img src="/Images/Home/Banner/spocodoc-logo.svg" alt="SpocoDoc" loading="lazy" decoding="async" />
            </div>
            <span className="section-tag light">Our Product</span>
            <span className="spocodoc-eyebrow">AP Automation · Budget Control · Approval Governance</span>
            <h2 className="section-title light">Control Spend. Automate Invoices. Govern Approvals.</h2>
            <p className="spocodoc-spotlight__desc">SpocoDoc gives finance teams control over the full invoice and spend process - from capture and departmental coding to supplier governance, approval routing, budget visibility and audit history.</p>
            <div className="spocodoc-spotlight__features">
              <span className="spocodoc-pill"><FaCheckCircle /> AI-assisted invoice capture and processing</span>
              <span className="spocodoc-pill"><FaCheckCircle /> Department-led coding and approval routing</span>
              <span className="spocodoc-pill"><FaCheckCircle /> Supplier governance and approval controls</span>
              <span className="spocodoc-pill"><FaCheckCircle /> Real-time budget and spend visibility</span>
              <span className="spocodoc-pill"><FaCheckCircle /> Complete audit trail for every invoice and decision</span>
            </div>
            <a href="https://spocodoc.com" target="_blank" rel="noopener noreferrer" className="btn-primary light" onClick={() => trackCTAClick('Explore SpocoDoc', 'spocodoc-spotlight')}>Explore SpocoDoc <FaArrowRight /></a>
          </FadeIn>
          <FadeIn className="spocodoc-spotlight__visual" delay={0.2}>
            <figure className="spocodoc-product-showcase">
              <img src="/Images/Home/Spocodoc/HomeSpocodoc.png" alt="SpocoDoc product interface for invoice automation, budget control and approval governance" loading="lazy" decoding="async" />
            </figure>
          </FadeIn>
        </div>
      </section>

      {/* HOW WE WORK — 3 step process */}
      <section className="how-we-work">
        <div className="section-inner">
          <FadeIn>
            <span className="section-tag">How We Work</span>
            <h2 className="section-title">From Problem to Solution in Three Steps</h2>
            <p className="section-subtitle">No jargon, no lengthy procurement. Just a clear path from challenge to measurable improvement.</p>
          </FadeIn>
          <div className="process-steps">
            <FadeIn className="process-step" delay={0.1}>
              <div className="process-step__number">1</div>
              <div className="process-step__icon"><FaSearchDollar /></div>
              <h3>Understand</h3>
              <p>We start with a free consultation to understand what's not working - listening to your challenges, auditing your systems, and finding the real root causes.</p>
            </FadeIn>
            <div className="process-connector" />
            <FadeIn className="process-step" delay={0.2}>
              <div className="process-step__number">2</div>
              <div className="process-step__icon"><FaClipboardCheck /></div>
              <h3>Design</h3>
              <p>We propose a tailored solution - whether that's a platform like SpocoDoc, a custom integration, an automation, or a strategic roadmap. You approve before we build.</p>
            </FadeIn>
            <div className="process-connector" />
            <FadeIn className="process-step" delay={0.3}>
              <div className="process-step__number">3</div>
              <div className="process-step__icon"><FaRocket /></div>
              <h3>Deliver</h3>
              <p>We build, test, and go live - with hands-on support and measurable outcomes. Most engagements show ROI within the first quarter.</p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH — industries and roles */}
      <section className="who-we-work-with">
        <div className="section-inner">
          <FadeIn>
            <span className="section-tag">Who We Help</span>
            <h2 className="section-title">Built for Businesses Like Yours</h2>
            <p className="section-subtitle">We work with SMEs and mid-market organisations across the UK - typically 20 to 500 employees - who need IT that actually delivers.</p>
          </FadeIn>
          <div className="audience-grid">
            <FadeIn className="audience-card" delay={0.05}>
              <h4>Industries</h4>
              <ul>
                <li><FaBuilding /> Professional services</li>
                <li><FaShoppingCart /> Retail &amp; eCommerce</li>
                <li><FaIndustry /> Manufacturing &amp; supply chain</li>
                <li><FaUniversity /> Education &amp; non-profit</li>
                <li><FaHandshake /> Consulting &amp; agencies</li>
              </ul>
            </FadeIn>
            <FadeIn className="audience-card" delay={0.15}>
              <h4>Who We Typically Speak To</h4>
              <ul>
                <li><FaUserTie /> CEOs &amp; Managing Directors</li>
                <li><FaChartLine /> CFOs &amp; Finance Directors</li>
                <li><FaCogs /> COOs &amp; Operations Managers</li>
                <li><FaLaptopCode /> IT Directors &amp; CTOs</li>
                <li><FaUsers /> Board Advisory Committees</li>
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* SAVINGS / ROI */}
      <section className="savings-section">
        <div className="section-inner">
          <div className="impact-layout">
            <FadeIn className="savings-text">
              <span className="section-tag">The Impact</span>
              <h2 className="section-title">Technology That Delivers Measurable Value</h2>
              <p>We establish the baseline, identify where technology can make the biggest difference, and measure the results after delivery.</p>
            </FadeIn>
            <FadeIn className="impact-outcomes" delay={0.12}>
              <div className="impact-outcome">
                <FaRobot />
                <h3>Reduce manual effort</h3>
                <p>Automate repetitive processing, approvals and data entry.</p>
              </div>
              <div className="impact-outcome">
                <FaCogs />
                <h3>Lower operational cost</h3>
                <p>Remove duplicated systems, inefficient licensing and unnecessary manual work.</p>
              </div>
              <div className="impact-outcome">
                <FaChartLine />
                <h3>Improve decision making</h3>
                <p>Bring operational and financial data together so teams can act on current information.</p>
              </div>
              <div className="impact-outcome">
                <FaShieldAlt />
                <h3>Reduce errors and risk</h3>
                <p>Use controlled workflows, validation and system integration instead of repeated manual entry.</p>
              </div>
            </FadeIn>
          </div>
          <FadeIn className="measurement-journey" delay={0.18}>
            <div className="measurement-step">
              <span>Baseline</span>
              <p>Understand current time, cost, effort and error levels.</p>
            </div>
            <div className="measurement-line" aria-hidden="true"></div>
            <div className="measurement-step">
              <span>Improve</span>
              <p>Automate, integrate and simplify.</p>
            </div>
            <div className="measurement-line" aria-hidden="true"></div>
            <div className="measurement-step">
              <span>Measure</span>
              <p>Compare actual outcomes after delivery.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* BUILD BANNER */}
      <section className="custom-banner">
        <div className="custom-banner__inner">
          <FadeIn>
            <h2>We Don't Just Consult. We Build.</h2>
            <p>From fully custom web applications to SaaS platforms like SpocoDoc, we develop solutions engineered for your unique business processes. Our development team works with Microsoft Azure, Power Platform, React, .NET, and modern cloud technologies to deliver production-grade software - fast.</p>
            <Link to="/contact" onClick={() => trackCTAClick('Lets Build Something Together', 'build-banner')} className="btn-primary">Let's Build Something Together <FaArrowRight /></Link>
          </FadeIn>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="final-cta">
        <FadeIn>
          <h2>Ready to Fix What's Broken?</h2>
          <p>Whether it's runaway costs, manual chaos, or systems that don't connect - we've seen it all and we know how to fix it.</p>
          <Link to="/contact" onClick={() => trackCTAClick('Get in Touch', 'final-cta')} className="btn-primary large">Get in Touch <FaArrowRight /></Link>
        </FadeIn>
      </section>
    </div>
  );
}

export default Home;
